import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowRight, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Latest-from-Shopperden strip — top 3 published blog posts.
 *
 * Drops above any dashboard tab content to keep blog discovery high.
 * Without this, the 60+ SEO posts only get traffic from the header
 * Blog link, which is invisible to logged-in users in the dashboard
 * shell. This strip turns the buyer dashboard into a content surface
 * too, lifting time-on-site and Google's E-E-A-T signal.
 */
const LatestBlogStrip = ({ title = 'Latest from Shopperden', dataTestId = 'latest-blog-strip' }) => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/blog/posts?published_only=true`);
        const items = Array.isArray(r.data) ? r.data : (r.data?.posts || []);
        if (!cancelled) setPosts(items.slice(0, 3));
      } catch { /* silent — blog discovery is a nice-to-have, not critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!posts.length) return null;

  return (
    <div className="mb-12" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <BookOpen className="h-4 w-4 text-[#E07A3A]" />
          <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A]">{title}</h3>
        </div>
        <Link to="/blog" className="text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#E07A3A] flex items-center gap-1" data-testid={`${dataTestId}-view-all`}>
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {posts.map((p) => (
          <Link
            to={`/blog/${p.slug}`}
            key={p.slug}
            className="group bg-white border border-slate-200 rounded p-5 hover:border-[#E07A3A] hover:shadow-sm transition-all"
            data-testid={`${dataTestId}-post-${p.slug}`}
          >
            {p.cover_image && (
              <div className="aspect-[16/9] -m-5 mb-4 overflow-hidden rounded-t bg-slate-100">
                <img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#E07A3A] mb-1.5 font-semibold">
              {p.category || 'Guide'}
            </p>
            <h4 className="font-['Playfair_Display'] text-base text-[#0F172A] mb-2 leading-snug group-hover:text-[#E07A3A] transition-colors">
              {p.title}
            </h4>
            <p className="text-xs text-slate-500 line-clamp-2">{p.excerpt}</p>
            <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100">
              <Clock className="h-3 w-3" />
              {p.published_at ? new Date(p.published_at).toLocaleDateString() : 'New'}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default LatestBlogStrip;
